import { useState, useEffect, useCallback } from "react";
import { NavLink, useLocation, useNavigate } from "react-router-dom";
import { supabase } from "@/lib/supabaseClient";
import { clearModuleCache, getModuleAccessMap } from "@/lib/hasModuleAccess";
import { getTenant } from "@/lib/getTenant";

/* ─── Types ──────────────────────────────────────────────────── */
interface TenantInfo {
  name: string;
  plan_code: string;
  status: string;
}

interface CoreItem {
  path: string;
  label: string;
  icon: string;
  end?: boolean;
  children?: { path: string; label: string; icon: string; end?: boolean }[];
}

interface AddonItem {
  moduleId: string;
  path: string;
  label: string;
  icon: string;
}

interface Props {
  isOpen?: boolean;
  onClose?: () => void;
}

/* ─── Core nav (always visible) ─────────────────────────────── */
const CORE_GROUPS: { groupLabel: string; items: CoreItem[] }[] = [
  {
    groupLabel: "Business",
    items: [
      { path: "/admin",               label: "Dashboard",     icon: "ri-dashboard-line",          end: true },
      { path: "/admin/clients",       label: "Clients",       icon: "ri-user-3-line" },
      { path: "/admin/leads",         label: "Leads",         icon: "ri-user-received-line" },
      { path: "/admin/appointments",  label: "Appointments",  icon: "ri-calendar-event-line" },
      {
        path: "/admin/contracts",
        label: "Contracts",
        icon: "ri-file-text-line",
        children: [
          { path: "/admin/contracts",           label: "All Contracts", icon: "ri-file-list-3-line",   end: true },
          { path: "/admin/contracts/templates", label: "Templates",     icon: "ri-file-shield-2-line" },
        ],
      },
      { path: "/admin/payments",      label: "Payments",      icon: "ri-money-dollar-circle-line" },
      { path: "/admin/jobs",          label: "Jobs / Shoots", icon: "ri-camera-3-line" },
    ],
  },
  {
    groupLabel: "Website",
    items: [
      { path: "/admin/website", label: "Website Editor", icon: "ri-edit-2-line", end: true },
      { path: "/admin/seo",     label: "SEO Manager",    icon: "ri-search-eye-line" },
    ],
  },
  {
    groupLabel: "Settings",
    items: [
      { path: "/admin/settings/branding",     label: "Branding",     icon: "ri-palette-line" },
      { path: "/admin/settings/integrations", label: "Integrations", icon: "ri-plug-line" },
      { path: "/admin/settings/payments",     label: "Payments",     icon: "ri-bank-card-line" },
      { path: "/admin/notifications",          label: "Notifications", icon: "ri-notification-3-line" },
      { path: "/admin/email-queue",   label: "Email Queue",    icon: "ri-mail-line" },
    ],
  },
];

/* ─── Add-on definitions ─────────────────────────────────────── */
const ADDONS: AddonItem[] = [
  { moduleId: "digital_invitations",    path: "/admin/invitations",           label: "Invitaciones",      icon: "ri-mail-open-line" },
  { moduleId: "digital_invitations",    path: "/admin/invitations/templates", label: "\u00a0\u00a0Templates",         icon: "ri-image-2-line" },
  { moduleId: "digital_invitations",    path: "/admin/invitations/music",     label: "\u00a0\u00a0M\u00fasica",             icon: "ri-music-2-line" },
  { moduleId: "signature_book",         path: "/admin/signature-books",       label: "Libros de Firmas",  icon: "ri-book-open-line" },
  { moduleId: "photo_selection",        path: "/admin/jobs",                label: "Photo Selection",   icon: "ri-image-2-line" },
  { moduleId: "final_deliveries",       path: "/admin/jobs",                label: "Final Deliveries",  icon: "ri-send-plane-line" },
  { moduleId: "media_packages",         path: "/admin/media-packages",      label: "Media Packages",    icon: "ri-disc-line" },
  { moduleId: "ai_sales_assistant",     path: "/admin/settings/ai-assistant", label: "AI Assistant",   icon: "ri-robot-line" },
  { moduleId: "ai_sales_assistant",     path: "/admin/ai-conversations",      label: "AI Conversations", icon: "ri-chat-3-line" },
  { moduleId: "email_automations",      path: "/admin/automations",         label: "Automations",       icon: "ri-flashlight-line" },
  { moduleId: "whatsapp_notifications", path: "/admin/reminders",           label: "WhatsApp / Reminders", icon: "ri-whatsapp-line" },
  { moduleId: "custom_domain",          path: "/admin/website",             label: "Custom Domain",     icon: "ri-server-line" },
];

/* ─── Component ──────────────────────────────────────────────── */
export default function AdminSidebar({ isOpen = false, onClose }: Props) {
  const location = useLocation();
  const navigate = useNavigate();

  const [tenantInfo, setTenantInfo]       = useState<TenantInfo | null>(null);
  const [moduleMap, setModuleMap]         = useState<Record<string, boolean>>();
  const [modulesLoaded, setModulesLoaded] = useState(false);
  const [addonsExpanded, setAddonsExpanded] = useState(false);

  /* ── Initial open state for accordion items ── */
  const allAccordionPaths = CORE_GROUPS
    .flatMap((g) => g.items)
    .filter((i) => i.children)
    .map((i) => i.path);

  const [openGroups, setOpenGroups] = useState<Set<string>>(
    new Set(allAccordionPaths.filter((p) => location.pathname.startsWith(p))),
  );

  /* ── Load tenant + module access ── */
  const loadData = useCallback(async () => {
    const [tenant] = await Promise.all([getTenant()]);
    if (tenant) {
      setTenantInfo({ name: tenant.name, plan_code: tenant.plan_code, status: tenant.status });
    }

    const addonIds = ADDONS.map((a) => a.moduleId);
    const map = await getModuleAccessMap(addonIds);
    setModuleMap(map);
    setModulesLoaded(true);

    const anyActive = addonIds.some((id) => map[id]);
    if (anyActive) setAddonsExpanded(true);
  }, []);

  useEffect(() => { loadData(); }, [loadData]);

  /* ── Accordion toggle ── */
  const toggleGroup = (path: string) => {
    setOpenGroups((prev) => {
      const next = new Set(prev);
      if (next.has(path)) next.delete(path);
      else next.add(path);
      return next;
    });
  };

  /* ── Logout ── */
  const handleLogout = async () => {
    await supabase.auth.signOut();
    clearModuleCache();
    navigate("/login");
  };

  const handleNavClick = () => { if (onClose) onClose(); };

  /* ── Derived add-on lists ── */
  const safeModuleMap = moduleMap ?? {};
  const activeAddons   = ADDONS.filter((a) => safeModuleMap[a.moduleId]);
  const inactiveAddons = ADDONS.filter((a) => !safeModuleMap[a.moduleId]);

  /* ─── Render helpers ─────────────────────────────────────── */
  const renderCoreItem = (item: CoreItem) => {
    if (item.children) {
      const isOpen = openGroups.has(item.path);
      const isGroupActive = location.pathname.startsWith(item.path);
      return (
        <div key={item.path}>
          <button
            onClick={() => toggleGroup(item.path)}
            className={`w-full flex items-center justify-between gap-3 px-3 py-2 rounded-lg text-sm transition-all cursor-pointer whitespace-nowrap ${
              isGroupActive
                ? "text-[#0f172a] bg-slate-100"
                : "text-slate-500 hover:text-[#0f172a] hover:bg-slate-50"
            }`}
          >
            <div className="flex items-center gap-3">
              <span className="w-4 h-4 flex items-center justify-center">
                <i className={item.icon}></i>
              </span>
              <span>{item.label}</span>
            </div>
            <i className={`text-xs transition-transform ${isOpen ? "ri-arrow-up-s-line" : "ri-arrow-down-s-line"}`}></i>
          </button>
          {isOpen && (
            <div className="ml-7 mt-0.5 space-y-0.5">
              {item.children.map((child) => (
                <NavLink
                  key={child.path}
                  to={child.path}
                  end={child.end}
                  onClick={handleNavClick}
                  className={({ isActive }) =>
                    `flex items-center gap-2.5 px-3 py-2 rounded-lg text-xs transition-all whitespace-nowrap ${
                      isActive
                        ? "text-[#2563eb] bg-blue-50"
                        : "text-slate-400 hover:text-slate-600 hover:bg-slate-50"
                    }`
                  }
                >
                  <span className="w-3.5 h-3.5 flex items-center justify-center">
                    <i className={child.icon}></i>
                  </span>
                  {child.label}
                </NavLink>
              ))}
            </div>
          )}
        </div>
      );
    }

    return (
      <NavLink
        key={item.path}
        to={item.path}
        end={item.end}
        onClick={handleNavClick}
        className={({ isActive }) =>
          `flex items-center gap-3 px-3 py-2 rounded-lg text-sm transition-all whitespace-nowrap ${
            isActive
              ? "text-[#2563eb] bg-blue-50 font-medium"
              : "text-slate-500 hover:text-[#0f172a] hover:bg-slate-50"
          }`
        }
      >
        <span className="w-4 h-4 flex items-center justify-center">
          <i className={item.icon}></i>
        </span>
        {item.label}
      </NavLink>
    );
  };

  const renderActiveAddon = (addon: AddonItem) => (
    <NavLink
      key={addon.path}
      to={addon.path}
      onClick={handleNavClick}
      className={({ isActive }) =>
        `flex items-center gap-3 px-3 py-2 rounded-lg text-sm transition-all whitespace-nowrap ${
          isActive
            ? "text-[#2563eb] bg-blue-50 font-medium"
            : "text-slate-500 hover:text-[#0f172a] hover:bg-slate-50"
        }`
      }
    >
      <span className="w-4 h-4 flex items-center justify-center">
        <i className={addon.icon}></i>
      </span>
      <span className="flex-1 truncate">{addon.label}</span>
      <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 shrink-0"></span>
    </NavLink>
  );

  const renderLockedAddon = (addon: AddonItem) => (
    <NavLink
      key={addon.path}
      to={addon.path}
      onClick={handleNavClick}
      className="flex items-center gap-3 px-3 py-2 rounded-lg text-sm transition-all whitespace-nowrap text-slate-300 hover:text-slate-500 hover:bg-slate-50 cursor-pointer"
    >
      <span className="w-4 h-4 flex items-center justify-center">
        <i className={addon.icon}></i>
      </span>
      <span className="flex-1 truncate">{addon.label}</span>
      <span className="w-4 h-4 flex items-center justify-center text-slate-300 shrink-0">
        <i className="ri-lock-line text-xs"></i>
      </span>
    </NavLink>
  );

  /* ─── Render ─────────────────────────────────────────────── */
  return (
    <aside
      className={[
        "w-64 bg-white flex flex-col border-r border-slate-200 shrink-0",
        "lg:relative lg:min-h-screen",
        "fixed inset-y-0 left-0 z-50 h-full overflow-y-auto",
        "transform transition-transform duration-300 ease-in-out",
        isOpen ? "translate-x-0" : "-translate-x-full lg:translate-x-0",
      ].join(" ")}
    >
      {/* Mobile close */}
      <button
        onClick={onClose}
        className="lg:hidden absolute top-4 right-4 w-8 h-8 flex items-center justify-center text-slate-400 hover:text-slate-700 rounded-lg transition-colors cursor-pointer z-10"
        aria-label="Close menu"
      >
        <i className="ri-close-line text-lg"></i>
      </button>

      {/* Logo */}
      <div className="px-6 py-5 border-b border-slate-200">
        <div className="flex items-center gap-3 pr-8 lg:pr-0">
          <img
            src="https://static.readdy.ai/image/e50d925263add9c47b947a5709171c8c/6cb7fb8d08f57254bfb69080e4b6eb8a.png"
            alt="Pixory AI"
            className="h-8 w-auto object-contain"
          />
          <div>
            <p className="text-[#0f172a] text-xs font-semibold tracking-widest">Pixory AI</p>
            <p className="text-slate-400 text-[10px] tracking-wide">Admin Panel</p>
          </div>
        </div>
      </div>

      {/* Tenant Badge */}
      <div className="mx-4 mt-4 mb-2 px-3 py-2.5 rounded-lg bg-[#C8A96E]/10 border border-[#C8A96E]/20">
        <p className="text-[#C8A96E] text-xs font-semibold truncate">
          {tenantInfo?.name ?? (
            <span className="inline-block w-24 h-3 bg-[#C8A96E]/20 rounded animate-pulse"></span>
          )}
        </p>
        <div className="flex items-center gap-1.5 mt-0.5">
          <span className={`w-1.5 h-1.5 rounded-full inline-block ${
            tenantInfo?.status === "active" ? "bg-emerald-500" : "bg-amber-500"
          }`}></span>
          <p className="text-slate-500 text-[10px] capitalize">
            {tenantInfo ? `${tenantInfo.plan_code} Plan · ${tenantInfo.status}` : "..."}
          </p>
        </div>
      </div>

      {/* Navigation */}
      <nav className="flex-1 px-3 py-3 overflow-y-auto space-y-4">

        {/* ── Core groups ── */}
        {CORE_GROUPS.map((group) => (
          <div key={group.groupLabel}>
            <p className="px-3 mb-1 text-[10px] font-semibold uppercase tracking-widest text-slate-400 select-none">
              {group.groupLabel}
            </p>
            <div className="space-y-0.5">
              {group.items.map((item) => renderCoreItem(item))}
            </div>
          </div>
        ))}

        {/* ── Add-ons section ── */}
        {modulesLoaded && (
          <div>
            {/* Section header — collapsible */}
            <button
              onClick={() => setAddonsExpanded((v) => !v)}
              className="w-full flex items-center justify-between px-3 mb-1 group cursor-pointer"
            >
              <div className="flex items-center gap-2">
                <p className="text-[10px] font-semibold uppercase tracking-widest text-slate-400 select-none group-hover:text-slate-500 transition-colors">
                  Add-ons
                </p>
                {activeAddons.length > 0 && (
                  <span className="px-1.5 py-0.5 rounded-full text-[9px] font-bold bg-emerald-100 text-emerald-600">
                    {activeAddons.length}
                  </span>
                )}
              </div>
              <i className={`text-[10px] text-slate-300 group-hover:text-slate-500 transition-all ${
                addonsExpanded ? "ri-arrow-up-s-line" : "ri-arrow-down-s-line"
              }`}></i>
            </button>

            {addonsExpanded && (
              <div className="space-y-0.5">
                {/* Active add-ons */}
                {activeAddons.map((addon) => renderActiveAddon(addon))}

                {/* Divider between active and locked (only if both exist) */}
                {activeAddons.length > 0 && inactiveAddons.length > 0 && (
                  <div className="mx-3 my-2 border-t border-slate-100" />
                )}

                {/* Locked add-ons */}
                {inactiveAddons.length > 0 && (
                  <>
                    {inactiveAddons.map((addon) => renderLockedAddon(addon))}

                    {/* Upgrade hint */}
                    <div className="mx-1 mt-2 px-3 py-2.5 rounded-lg bg-[#C8A96E]/5 border border-[#C8A96E]/10">
                      <div className="flex items-center gap-2 mb-1">
                        <span className="w-3.5 h-3.5 flex items-center justify-center text-[#C8A96E]/50">
                          <i className="ri-sparkling-line text-xs"></i>
                        </span>
                        <p className="text-[#C8A96E]/60 text-[10px] font-semibold uppercase tracking-wider">
                          {inactiveAddons.length} available
                        </p>
                      </div>
                      <p className="text-slate-400 text-[10px] leading-relaxed">
                        Click any locked item to request activation from your Super Admin.
                      </p>
                    </div>
                  </>
                )}

                {/* All active — no locked items */}
                {inactiveAddons.length === 0 && activeAddons.length > 0 && (
                  <div className="mx-1 mt-1 px-3 py-2 rounded-lg bg-emerald-50 border border-emerald-100">
                    <div className="flex items-center gap-2">
                      <span className="w-3.5 h-3.5 flex items-center justify-center text-emerald-500/60">
                        <i className="ri-checkbox-circle-line text-xs"></i>
                      </span>
                      <p className="text-emerald-600 text-[10px] font-medium">All add-ons active</p>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        {/* Loading skeleton for add-ons */}
        {!modulesLoaded && (
          <div>
            <p className="px-3 mb-1 text-[10px] font-semibold uppercase tracking-widest text-slate-400 select-none">
              Add-ons
            </p>
            <div className="space-y-1 px-3">
              {[1, 2, 3].map((i) => (
                <div key={i} className="h-8 bg-slate-100 rounded-lg animate-pulse" />
              ))}
            </div>
          </div>
        )}

      </nav>

      {/* Footer */}
      <div className="px-4 py-4 border-t border-slate-200 flex flex-col gap-2">
        <NavLink
          to="/"
          onClick={handleNavClick}
          className="flex items-center gap-2 text-slate-400 hover:text-slate-600 text-xs transition-colors cursor-pointer whitespace-nowrap"
        >
          <span className="w-4 h-4 flex items-center justify-center">
            <i className="ri-arrow-left-line"></i>
          </span>
          Back to Landing
        </NavLink>
        <button
          onClick={handleLogout}
          className="flex items-center gap-2 text-red-500/60 hover:text-red-600 text-xs transition-colors cursor-pointer whitespace-nowrap w-full"
        >
          <span className="w-4 h-4 flex items-center justify-center">
            <i className="ri-logout-box-line"></i>
          </span>
          Cerrar sesión
        </button>
      </div>
    </aside>
  );
}